/**
 * src/components/Financial/EphemerisChartSection.jsx
 * 【完全修正版：赤緯列の削除・黄経Lon表示・サイン角度詳細・isMobile定義・ダークモード対応】
 */
import React, { useMemo, useState, useEffect } from "react";
import * as Astro from "../../astronomy.js";
import { styles as commonStyles } from "../../utils/styles.js";
import {
  PLANET_SYMBOLS,
  PLANETS,
  PLANET_COLORS,
} from "../../utils/constants.js";
import { Compass, Table } from "lucide-react";

export default function EphemerisChartSection(props) {
  // --- 1. プロップスの正規化と初期化 ---
  var positions = props.positions || {};
  var date = useMemo(
    function () {
      return props.transitDate ? new Date(props.transitDate) : new Date();
    },
    [props.transitDate],
  );

  var planetColors = props.planetColors || PLANET_COLORS || {};
  var signColors = props.signColors || {};
  var isHeliocentric =
    props.isHeliocentric === true || props.isHeliocentric === "true";
  var isRetro =
    props.isRetro ||
    function () {
      return false;
    };
  var aspectSettings = props.aspectSettings || { majorOrb: 8 };

  // --- 2. レスポンシブ判定 (isMobile の定義) ---
  var [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );
  useEffect(function () {
    var handleResize = function () {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return function () {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  var isMobile = windowWidth <= 1024;

  // --- 3. ダークモード判定とテーマ設定 ---
  var isDark = (function () {
    var p = props.isDarkMode;
    if (p === true || p === "true" || p === "dark") return true;
    if (typeof document !== "undefined") {
      var el = document.documentElement;
      var bd = document.body;
      if (
        el.classList.contains("dark") ||
        bd.classList.contains("dark") ||
        el.getAttribute("data-theme") === "dark"
      )
        return true;
    }
    return false;
  })();

  var theme = {
    cardBg: isDark ? "#1c1f26" : "#ffffff",
    subBg: isDark ? "rgba(255, 255, 255, 0.03)" : "#f8fafc",
    border: isDark ? "#2d313d" : "#e2e8f0",
    text: isDark ? "#ffffff" : "#1e293b",
    subText: isDark ? "#9ca3af" : "#64748b",
  };

  // フォーマット関数: DD°MM′SS″
  var formatFullDMS = function (deg) {
    if (deg === undefined || deg === null || isNaN(deg)) return "0°00′00″";
    var absDeg = Math.abs(deg);
    var d = Math.floor(absDeg);
    var m = Math.floor((absDeg % 1) * 60);
    var s = Math.floor((((absDeg % 1) * 60) % 1) * 60);
    return (
      (deg < 0 ? "-" : "") +
      d +
      "°" +
      m.toString().padStart(2, "0") +
      "′" +
      s.toString().padStart(2, "0") +
      "″"
    );
  };

  // チャート描画定数
  var size = 640;
  var center = size / 2;
  var chartRadius = 220;
  var tickLabelRadius = 245;
  var signRadius = 280;

  var SIGNS = [
    { symbol: "♈︎", name: "Aries", start: 0 },
    { symbol: "♉︎", name: "Taurus", start: 30 },
    { symbol: "♊︎", name: "Gemini", start: 60 },
    { symbol: "♋︎", name: "Cancer", start: 90 },
    { symbol: "♌︎", name: "Leo", start: 120 },
    { symbol: "♍︎", name: "Virgo", start: 150 },
    { symbol: "♎︎", name: "Libra", start: 180 },
    { symbol: "♏︎", name: "Scorpio", start: 210 },
    { symbol: "♐︎", name: "Sagittarius", start: 240 },
    { symbol: "♑︎", name: "Capricorn", start: 270 },
    { symbol: "♒︎", name: "Aquarius", start: 300 },
    { symbol: "♓︎", name: "Pisces", start: 330 },
  ];

  var polarToCartesian = function (deg, r) {
    var rad = ((deg - 180) * Math.PI) / 180;
    return { x: center + r * Math.cos(rad), y: center + r * Math.sin(rad) };
  };

  var getScaledDistance = function (planetId) {
    var dist = 1.0;
    try {
      dist = Astro.getPlanetDistance(planetId, date, isHeliocentric);
    } catch (e) {
      var avgDist = {
        Sun: 0.01,
        Moon: 0.15,
        Mercury: 0.39,
        Venus: 0.72,
        Earth: 1.0,
        Mars: 1.52,
        Jupiter: 5.2,
        Saturn: 9.54,
        Uranus: 19.2,
        Neptune: 30.1,
        Pluto: 39.5,
      };
      dist = avgDist[planetId] || 1.0;
    }
    var minR = 45;
    var scaled = minR + Math.sqrt(dist) * 27;
    return Math.min(chartRadius - 5, scaled);
  };

  var planetCoords = useMemo(
    function () {
      var coords = {};
      var targetPlanets = isHeliocentric
        ? [
          "Earth",
          "Mercury",
          "Venus",
          "Mars",
          "Jupiter",
          "Saturn",
          "Uranus",
          "Neptune",
          "Pluto",
        ]
        : PLANETS.slice(0, 10);
      targetPlanets.forEach(function (p) {
        var lon = positions[p];
        if (lon !== null && lon !== undefined) {
          var d = getScaledDistance(p);
          coords[p] = Object.assign({}, polarToCartesian(lon, d), {
            dist: d,
            lon: lon,
          });
        }
      });
      return coords;
    },
    [positions, date, isHeliocentric],
  );

  var aspectLines = useMemo(
    function () {
      var lines = [];
      var pKeys = Object.keys(planetCoords);
      var orb = aspectSettings.majorOrb;
      for (var i = 0; i < pKeys.length; i++) {
        for (var j = i + 1; j < pKeys.length; j++) {
          var p1 = pKeys[i],
            p2 = pKeys[j];
          var diff = Math.abs(positions[p1] - positions[p2]) % 360;
          var dist = diff > 180 ? 360 - diff : diff;
          var color = "";
          if (Math.abs(dist - 0) < orb) color = "#10b981";
          else if (Math.abs(dist - 180) < orb) color = "#ef4444";
          else if (Math.abs(dist - 90) < orb) color = "#f59e0b";
          else if (Math.abs(dist - 120) < orb) color = "#3b82f6";
          else if (Math.abs(dist - 60) < orb) color = "#6366f1";
          if (color !== "") lines.push({ p1: p1, p2: p2, color: color });
        }
      }
      return lines;
    },
    [positions, planetCoords, aspectSettings],
  );

  var ticks = [];
  for (var i = 0; i < 360; i += 5) {
    ticks.push(i);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* ページ上部：戻るボタン横の見出し */}
      <div
        style={{
          display: "flex",
          alignItems: "center", // 垂直方向の中央揃え
          gap: "12px",
          marginTop: "-15px", // 戻るボタンの高さに合わせるための調整
          marginBottom: "15px",
          paddingLeft: "48px", // 戻るボタンの幅を確保
        }}
      >
        <Compass size={24} color="rgb(147, 39, 143)" />
        <h1
          style={{
            fontSize: "22px",
            fontWeight: "bold",
            color: theme.text,
            margin: 0,
            lineHeight: "1", // テキストのベースラインをアイコンに合わせる
          }}
        >
          エフェリメス・チャート分析
        </h1>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(12, 1fr)",
          gap: "20px",
          background: "transparent",
        }}
      >
        {/* 1. エフェリメス・チャートブロック (span 6) */}
        <div
          style={{
            gridColumn: isMobile === true ? "span 12" : "span 6",
            background: theme.cardBg,
            borderRadius: "15px",
            border: "1px solid " + theme.border,
            padding: "30px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "25px",
              color: theme.text,
            }}
          >
            <Compass size={20} color="rgb(147, 39, 143)" />
            <span style={{ fontSize: "16px", fontWeight: "bold" }}>
              エフェリメス・チャート (
              {isHeliocentric ? "ヘリオ中心" : "ジオ中心"})
            </span>
          </div>

          <div
            style={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <svg
              width="100%"
              height="auto"
              viewBox={"0 0 " + size + " " + size}
              style={{ maxWidth: "500px" }}
            >
              <circle
                cx={center}
                cy={center}
                r={chartRadius}
                fill="none"
                stroke={theme.border}
                strokeWidth="1"
              />
              {ticks.map(function (deg) {
                var isMajor = deg % 30 === 0;
                var isMedium = deg % 10 === 0;
                var tLen = isMajor ? 12 : isMedium ? 8 : 4;
                var p1 = polarToCartesian(deg, chartRadius);
                var p2 = polarToCartesian(deg, chartRadius - tLen);
                return (
                  <line
                    key={"t-" + deg}
                    x1={p1.x}
                    y1={p1.y}
                    x2={p2.x}
                    y2={p2.y}
                    stroke={theme.subText}
                    strokeWidth={isMajor ? "1.5" : "1"}
                    opacity="0.5"
                  />
                );
              })}
              {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map(
                function (deg) {
                  var pos = polarToCartesian(deg, tickLabelRadius);
                  return (
                    <text
                      key={"lbl-" + deg}
                      x={pos.x}
                      y={pos.y}
                      fontSize="11"
                      fontWeight="bold"
                      fill={theme.subText}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      {deg}
                    </text>
                  );
                },
              )}
              {/* チャート内の星座：設定色を反映 */}
              {SIGNS.map(function (s, idx) {
                var pos = polarToCartesian(s.start + 15, signRadius);
                var pureSymbol = s.symbol.replace(/\uFE0E/g, "");
                // 記号または名称でカラー設定を検索し、なければデフォルト
                var color =
                  signColors[s.symbol] ||
                  signColors[pureSymbol] ||
                  signColors[s.name] ||
                  "#8b5cf6";
                return (
                  <text
                    key={idx}
                    x={pos.x}
                    y={pos.y}
                    fontSize="18"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={color}
                    style={{ userSelect: "none" }}
                  >
                    {s.symbol + "\uFE0E"}
                  </text>
                );
              })}
              {aspectLines.map(function (asp, idx) {
                var c1 = planetCoords[asp.p1],
                  c2 = planetCoords[asp.p2];
                if (c1 === undefined || c2 === undefined) return null;
                return (
                  <line
                    key={"asp-" + idx}
                    x1={c1.x}
                    y1={c1.y}
                    x2={c2.x}
                    y2={c2.y}
                    stroke={asp.color}
                    strokeWidth="1.5"
                    opacity="0.4"
                  />
                );
              })}
              {Object.keys(planetCoords).map(function (p) {
                var coord = planetCoords[p],
                  color = planetColors[p] || theme.text,
                  retro = isRetro(p);
                return (
                  <g key={p}>
                    <line
                      x1={center}
                      y1={center}
                      x2={coord.x}
                      y2={coord.y}
                      stroke={color}
                      strokeWidth="0.5"
                      strokeDasharray="3,3"
                      opacity="0.2"
                    />
                    <circle
                      cx={center} // 修正：center から描画
                      cy={center}
                      r={coord.dist} // 距離に応じて描画
                      fill="none"
                      stroke={theme.border}
                      strokeWidth="0.2"
                      opacity="0.1"
                    />
                    <circle
                      cx={coord.x}
                      cy={coord.y}
                      r="14"
                      fill={theme.cardBg}
                      stroke={color}
                      strokeWidth="1.5"
                    />
                    <text
                      x={coord.x}
                      y={coord.y + 5}
                      fontSize="16"
                      textAnchor="middle"
                      fill={color}
                    >
                      {PLANET_SYMBOLS[p] || p[0]}
                    </text>
                    <text
                      x={coord.x}
                      y={coord.y - 18}
                      fontSize="10"
                      fontWeight="bold"
                      textAnchor="middle"
                      fill={theme.subText}
                    >
                      {(coord.lon % 30).toFixed(1)}°{retro ? "(R)" : ""}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>

        {/* 2. 惑星サマリーブロック (span 6) */}
        <div
          style={{
            gridColumn: isMobile === true ? "span 12" : "span 6",
            background: theme.cardBg,
            borderRadius: "15px",
            border: "1px solid " + theme.border,
            padding: "30px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "25px",
              color: theme.text,
            }}
          >
            <Table size={20} color="rgb(147, 39, 143)" />
            <span style={{ fontSize: "16px", fontWeight: "bold" }}>
              惑星サマリー
            </span>
          </div>

          <div style={{ flex: 1, overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "12px",
                textAlign: "left",
              }}
            >
              <thead>
                <tr style={{ borderBottom: "1px solid " + theme.border }}>
                  <th
                    style={{
                      padding: "12px 8px",
                      color: theme.subText,
                      fontWeight: "bold",
                    }}
                  >
                    惑星
                  </th>
                  <th
                    style={{
                      padding: "12px 8px",
                      color: theme.subText,
                      fontWeight: "bold",
                      textAlign: "right",
                    }}
                  >
                    黄経 (Lon)
                  </th>
                  <th
                    style={{
                      padding: "12px 8px",
                      color: theme.subText,
                      fontWeight: "bold",
                      textAlign: "right",
                    }}
                  >
                    AU {isHeliocentric ? "(太陽からの距離)" : "(地球からの距離)"}
                  </th>
                  {/* 「サイン角度」ヘッダーを削除 */}
                  <th
                    style={{
                      padding: "12px 8px",
                      color: theme.subText,
                      fontWeight: "bold",
                      textAlign: "right",
                    }}
                  >
                    状態
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(planetCoords).map(function (p) {
                  var lon = positions[p];
                  var retro = isRetro(p);
                  var color = planetColors[p] || theme.text;
                  // 不要になったサイン計算用の変数を削除

                  return (
                    <tr
                      key={p}
                      style={{ borderBottom: "1px solid " + theme.border }}
                    >
                      <td style={{ padding: "12px 8px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "20px",
                              color: color,
                              width: "24px",
                              textAlign: "center",
                            }}
                          >
                            {PLANET_SYMBOLS[p] || p[0]}
                          </span>
                          <span
                            style={{ color: theme.text, fontWeight: "bold" }}
                          >
                            {p}
                          </span>
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "12px 8px",
                          textAlign: "right",
                          color: theme.text,
                        }}
                      >
                        {formatFullDMS(lon)}
                      </td>
                      <td
                        style={{
                          padding: "12px 8px",
                          textAlign: "right",
                          color: theme.text,
                          fontFamily: "monospace",
                        }}
                      >
                        {(() => {
                          try {
                            var dist = Astro.getPlanetDistance(p, date, isHeliocentric);
                            return dist.toFixed(4);
                          } catch (e) {
                            return "—";
                          }
                        })()}
                      </td>
                      {/* 「サイン角度」列のデータ (td) を削除 */}
                      <td style={{ padding: "12px 8px", textAlign: "right" }}>
                        <span
                          style={{
                            padding: "3px 8px",
                            borderRadius: "12px",
                            fontSize: "10px",
                            fontWeight: "bold",
                            whiteSpace: "nowrap",
                            background:
                              retro === true
                                ? "rgba(239, 68, 68, 0.1)"
                                : "rgba(16, 185, 129, 0.1)",
                            color: retro === true ? "#ef4444" : "#10b981",
                            border:
                              "1px solid " +
                              (retro === true ? "#ef4444" : "#10b981"),
                          }}
                        >
                          {retro === true ? "逆行" : "直行"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div> // ← ここに Flex コンテナーを閉じるための div を追加
  );
}
